import { useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

const isExternalLink = (href) => /^https?:\/\//i.test(String(href || ""));

const normalizeInternalLink = (href) => {
  const raw = String(href || "").trim();
  if (!raw) return "";
  if (raw.startsWith("/") || raw.startsWith("#")) return raw;
  return `/${raw}`;
};

const ArrowButton = ({ onClick, label, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="grid h-10 w-10 place-items-center rounded-full border border-white/28 bg-white/82 text-[var(--ink)] shadow-soft-sm backdrop-blur transition hover:bg-white"
  >
    {children}
  </button>
);

const alignClass = (align) => {
  const value = String(align || "left").toLowerCase();
  if (value === "center") return "items-center text-center";
  if (value === "right") return "items-end text-right";
  return "items-start text-left";
};

const titleClass = (size) => {
  const value = String(size || "h2").toLowerCase();
  if (value === "h1") return "text-[2rem] sm:text-3xl md:text-4xl";
  if (value === "h3") return "text-[1.65rem] sm:text-2xl md:text-3xl";
  return "text-[2rem] sm:text-3xl md:text-4xl";
};

export default function TopBannerCarousel({
  slides = [],
  fallbackCtaText = "View all",
  fallbackCtaLink = "/listings",
}) {
  const sliderRef = useRef(null);
  const items = Array.isArray(slides) ? slides.filter(Boolean) : [];
  const canSlide = items.length > 1;

  const settings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: canSlide,
      autoplay: canSlide,
      autoplaySpeed: 5200,
      speed: 650,
      slidesToShow: 1,
      slidesToScroll: 1,
      pauseOnHover: true,
      adaptiveHeight: false,
    }),
    [canSlide]
  );

  if (!items.length) return null;

  return (
    <div className="relative overflow-hidden rounded-[22px] border border-[var(--border)] shadow-soft-lg">
      {canSlide ? (
        <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
          <ArrowButton
            label="Previous banner"
            onClick={() => sliderRef.current?.slickPrev?.()}
          >
            {"\u2039"}
          </ArrowButton>
          <ArrowButton
            label="Next banner"
            onClick={() => sliderRef.current?.slickNext?.()}
          >
            {"\u203A"}
          </ArrowButton>
        </div>
      ) : null}

      <Slider ref={sliderRef} {...settings}>
        {items.map((slide) => {
          const rawHref =
            slide.ctaLink || slide.cta_link || slide.link || slide.url;
          const ctaText = slide.ctaText || slide.cta_text || "";
          const href = rawHref ? (isExternalLink(rawHref) ? rawHref : normalizeInternalLink(rawHref)) : "";
          const isExternal = href ? isExternalLink(href) : false;
          const Wrapper = href ? (isExternal ? "a" : Link) : "div";
          const wrapperProps = href
            ? isExternal
              ? { href, target: "_blank", rel: "noreferrer" }
              : { to: href }
            : {};

          const overlayOpacity =
            Number(slide?.meta?.overlayOpacity ?? slide?.meta?.overlay ?? 0.62) ||
            0.62;

          return (
            <div key={slide.id}>
              <Wrapper {...wrapperProps} className="block">
                <div className="relative h-[244px] sm:h-56 lg:h-64">
                  {slide.imageUrl ? (
                    <img
                      src={resolveMediaUrl(slide.imageUrl, {
                        width: 1280,
                        height: 360,
                        fit: "cover",
                        quality: 74,
                      })}
                      alt={slide.title || "Banner"}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="h-full w-full bg-slate-200" />
                  )}

                  <div
                    className="absolute inset-0 bg-gradient-to-r from-[rgba(7,20,36,0.9)] via-[rgba(7,20,36,0.52)] to-transparent"
                    style={{ opacity: Math.max(0.35, Math.min(0.95, overlayOpacity)) }}
                  />

                  <div className="absolute inset-0 flex items-end sm:items-center">
                    <div className="w-full px-5 sm:px-8">
                      <div
                        className={`max-w-[20rem] sm:max-w-xl flex flex-col gap-2 ${alignClass(
                          slide.textAlignment
                        )}`}
                        style={{ color: slide.textColor || "#ffffff" }}
                      >
                        {slide.meta?.kicker ? (
                          <p className="text-[10px] uppercase tracking-[0.24em] text-white/80 sm:text-xs sm:tracking-[0.3em]">
                            {slide.meta.kicker}
                          </p>
                        ) : null}

                        {slide.title ? (
                          <h3
                            className={`${titleClass(slide.textSize)} max-w-full font-homevilas leading-[1.02] [overflow-wrap:anywhere]`}
                            style={{ fontWeight: Number(slide.fontWeight || 600) }}
                          >
                            {slide.title}
                          </h3>
                        ) : null}

                        {slide.subtitle ? (
                          <p className="line-clamp-3 text-sm leading-6 text-white/84 md:text-base md:leading-relaxed">
                            {slide.subtitle}
                          </p>
                        ) : null}

                        {href && ctaText ? (
                          <span className="mt-1 inline-flex w-fit items-center rounded-full bg-[var(--cta-dark)] px-4 py-2 text-[10px] font-medium uppercase tracking-[0.22em] text-white shadow-soft-sm sm:mt-2 sm:tracking-[0.28em]">
                            {ctaText}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </Wrapper>
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

